import type { CardSlot } from '@kenocyber/shared';
import { PVP_TEAM_CODE_LENGTH, type PvpTeamChair, type PvpTeamLobby } from '@kenocyber/shared';
import { useState } from 'react';
import { PvpTeamHud } from './PvpTeamHud';
import type { PvpPresentation } from './selectors';
import { pvpTeamHudView } from './team-hud';
import { type PvpTeamUiState, teamCanLaunch, teamOwnsLobby } from './team-machine';
import { pvpTeamResultText } from './team-result-text';

/**
 * Everything the team match shows, and nothing it decides.
 *
 * ## What this component is not allowed to know
 *
 * There is no `fetch`, no socket, no ticket, no reducer and no timer here. It
 * receives a state and a set of flags and renders them; every action is a
 * callback the controller owns. The one piece of local state it keeps is the
 * CODE BEING TYPED, which is exactly the kind of thing a text field owns.
 *
 * ## Two codes, said plainly, and never merged
 *
 * The server hands the account that opened the room TWO codes with different
 * meanings: one seats the two remaining players on the creator's own side, the
 * other seats the three opponents. A single field labelled "invite code" would
 * be the interface losing the only distinction that matters - somebody would
 * paste the wrong one and find their friend on the other team, with no way to
 * undo it, because a chair is the server's to give and not a client's to swap.
 *
 * So they are two cards with two headings, two copy buttons and two occupancy
 * read-outs. Everybody else sees neither: `lobby.codes` is `null` for the other
 * five accounts, which is the server's decision and not a rendering choice here.
 *
 * ## A guest never chooses a side
 *
 * There is one field, it takes one code, and the code is all that is sent. There
 * is no team selector, no "swap sides" control, nothing draggable between the
 * columns, and no inference from the order people arrived in. Which side a code
 * opens is a column beside it on the server.
 *
 * ## Every side on this screen is relative to the reader
 *
 * "Sua equipe" is whichever chairs share `you.team` - the chair the SERVER gave
 * this account - so six browsers render six correct screens from the same
 * roster. Nothing here reads a chair's position in the array.
 */

export interface PvpTeamPanelProps {
  readonly state: PvpTeamUiState;
  readonly presentation: PvpPresentation;
  readonly onCreate: () => void;
  readonly onJoin: (code: string) => void;
  /** Called on the first edit after a refusal, so the message goes with it. */
  readonly onCodeEdited: () => void;
  readonly onLaunch: () => void;
  readonly onReady: () => void;
  readonly onLeave: () => void;
  readonly onRetryRecovery: () => void;
  readonly onArmTeleport: () => void;
  /** Manda rebentar as marcas deste jogador (gate V28G.N). */
  readonly onDetonateBleed: () => void;
  /** A ORDEM de ligar uma carta, do HUD para a cena (gate V28H.J). */
  readonly onActivateCard: (slot: CardSlot) => void;
  /** E a soltura da carga (gate V28H.N). */
  readonly onReleaseCard?: ((slot: CardSlot) => void) | undefined;
}

export function PvpTeamPanel({
  state,
  presentation,
  onArmTeleport: armTeleport,
  onDetonateBleed: detonateBleed,
  onActivateCard: activateCard,
  onReleaseCard: releaseCard,
  onCreate,
  onJoin,
  onCodeEdited,
  onLaunch,
  onReady,
  onLeave,
  onRetryRecovery,
}: PvpTeamPanelProps) {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState<'ally' | 'opponent' | null>(null);

  /*
   * The dedicated mode is a SURFACE, not an overlay - the same rule the duel
   * follows, because it is the same arena underneath.
   */
  const className = `${presentation.dedicatedMode ? 'pvp-surface' : 'pvp-panel'} pvp-team`;

  return (
    <section className={className} data-testid="pvp-team-panel" aria-label="Batalha em equipe 3x3">
      {presentation.dedicatedMode ? null : (
        <header className="pvp-panel-head">
          <h2>PVP PRIVADO 3X3</h2>
          <p className="pvp-no-reward" data-testid="pvp-team-no-reward">
            SEM RECOMPENSAS — nenhum Gold, XP ou item muda de mãos.
          </p>
        </header>
      )}

      <div className="pvp-panel-body">{body()}</div>

      {presentation.canClose ? (
        <>
          {/*
           * The intent never reached the room, so nothing was abandoned. The
           * chair is still in the fight and the button offers another attempt.
           */}
          {presentation.abandonFailed ? (
            <p className="pvp-error" role="alert" data-testid="pvp-team-abandon-error">
              Não foi possível abandonar a partida. Tente novamente.
            </p>
          ) : null}
          <button
            type="button"
            className="pvp-leave"
            data-testid="pvp-team-leave"
            onClick={onLeave}
            // Busy while the intent travels. The confirmation is the server's
            // `ended` frame and nothing else.
            disabled={presentation.abandonPending}
          >
            {leaveLabel()}
          </button>
        </>
      ) : null}
    </section>
  );

  /**
   * What the one exit button says right now.
   *
   * Same wording as the duel, and for the same reason: during a fight this
   * forfeits the match - here for three people at once - and "SAIR" reads like
   * closing a panel.
   */
  function leaveLabel(): string {
    if (presentation.abandonPending) {
      return 'ABANDONANDO…';
    }
    if (presentation.abandonFailed) {
      return 'TENTAR NOVAMENTE';
    }
    if (presentation.showsResult || presentation.showsError) {
      return 'RETORNAR AO JOGO';
    }
    return state.kind === 'running' ? 'ABANDONAR PARTIDA' : 'SAIR';
  }

  function body() {
    switch (state.kind) {
      case 'choosing':
        return (
          <div className="pvp-choice">
            {state.lastError ? (
              <p className="pvp-error" role="alert" data-testid="pvp-team-join-error">
                {errorText(state.lastError.reason)}
              </p>
            ) : null}
            <button
              type="button"
              className="pvp-create"
              data-testid="pvp-team-create"
              onClick={onCreate}
            >
              CRIAR SALA 3X3
            </button>
            <form
              className="pvp-join"
              onSubmit={(event) => {
                event.preventDefault();
                submitCode();
              }}
            >
              <label htmlFor="pvp-team-code">Código da sala</label>
              <input
                id="pvp-team-code"
                data-testid="pvp-team-code-input"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  // The refusal was about the old code. It goes with it.
                  onCodeEdited();
                }}
                /*
                 * The length the contract states, as a hint and a bound. The
                 * SERVER decides whether a code exists - and which TEAM it
                 * opens, which is not a thing this field can express.
                 */
                maxLength={PVP_TEAM_CODE_LENGTH}
                autoComplete="off"
                spellCheck={false}
                placeholder="ABCD234567"
              />
              <button type="submit" data-testid="pvp-team-join" disabled={code.trim().length === 0}>
                {state.lastError ? 'TENTAR NOVAMENTE' : 'ENTRAR NA SALA'}
              </button>
            </form>
            <p className="pvp-hint">
              Um código coloca você na equipe que o dono da sala escolheu ao enviá-lo.
            </p>
          </div>
        );

      case 'creating':
      case 'joining':
        return (
          <p className="pvp-busy" data-testid="pvp-team-busy">
            Falando com o servidor…
          </p>
        );

      case 'lobby':
        return lobbyBody(state.lobby, state.launching, state.lastError?.reason ?? null);

      case 'connecting':
        return (
          <p
            className="pvp-busy"
            data-testid={state.resumed ? 'pvp-team-reconnecting' : 'pvp-team-connecting'}
          >
            {state.resumed ? 'RECONECTANDO…' : 'Entrando na arena…'}
          </p>
        );

      case 'entering_pvp':
        return (
          <p className="pvp-busy" data-testid="pvp-team-connecting">
            Entrando na arena…
          </p>
        );

      case 'ready':
        return (
          <div className="pvp-ready">
            {/* The bars are already live: six ships exist and the room is
                complete. Only the clock is absent, because nothing is ticking. */}
            <PvpTeamHud
              view={pvpTeamHudView(state)}
              cardSlots={pvpTeamHudView(state).cardSlots}
              onArmTeleport={armTeleport}
              onDetonateBleed={detonateBleed}
              onActivateCard={activateCard}
              onReleaseCard={releaseCard}
            />
            <p data-testid="pvp-team-ready">Equipes completas.</p>
            <button
              type="button"
              data-testid="pvp-team-ready-button"
              onClick={onReady}
              /*
               * One per generation, and the flag says which. The room starts
               * when all six have said it - the confirmation comes from the
               * server, never from having pressed the button.
               */
              disabled={!presentation.canReady}
            >
              {presentation.canReady ? 'PRONTO' : 'AGUARDANDO O SERVIDOR…'}
            </button>
          </div>
        );

      case 'running':
      case 'reconnecting':
        return (
          <PvpTeamHud
            view={pvpTeamHudView(state)}
            cardSlots={pvpTeamHudView(state).cardSlots}
            onArmTeleport={armTeleport}
            onDetonateBleed={detonateBleed}
            onActivateCard={activateCard}
            onReleaseCard={releaseCard}
          />
        );

      case 'completed': {
        /*
         * Straight from the exhaustive mapping, against the team the SERVER put
         * in this client's own chair. No default, no invented winner, and never
         * a comparison against a chair or a roster position.
         */
        const text = pvpTeamResultText(state.result, state.match.team, state.match.participantId);
        return (
          <div className={`pvp-result pvp-result-${text.outcome}`} data-testid="pvp-team-result">
            <h3 data-testid="pvp-team-result-title">{text.title}</h3>
            <p data-testid="pvp-team-result-detail">{text.detail}</p>
            <p className="pvp-no-reward" data-testid="pvp-team-no-reward">
              SEM RECOMPENSAS — nenhum Gold, XP ou item mudou de mãos.
            </p>
          </div>
        );
      }

      case 'leaving':
        return (
          <p className="pvp-busy" data-testid="pvp-team-leaving">
            Encerrando a partida…
          </p>
        );

      case 'recovering_hunt':
        return (
          <div className="pvp-recovering" data-testid="pvp-team-recovering">
            <p>Retornando ao jogo…</p>
            {state.failure ? (
              <>
                <p className="pvp-error" role="alert">
                  Não foi possível retomar a caçada.
                </p>
                <button type="button" data-testid="pvp-team-retry" onClick={onRetryRecovery}>
                  TENTAR NOVAMENTE
                </button>
              </>
            ) : null}
          </div>
        );

      case 'error':
        return (
          <div className="pvp-error-screen" data-testid="pvp-team-error">
            <p role="alert">{errorText(state.error.reason)}</p>
          </div>
        );

      default:
        // `closed` never reaches here: the panel is not rendered at all.
        return null;
    }
  }

  function lobbyBody(lobby: PvpTeamLobby, launching: boolean, failure: string | null) {
    /*
     * MY side and THEIRS, from the chair the server gave this account.
     *
     * `you.team` and nothing else. Not the first three chairs, not the order the
     * rows arrived in, not which letter comes first in the alphabet: six people
     * read one roster and each of them has to see their own three on the left.
     */
    const mine = chairsOf(lobby, lobby.you.team);
    const theirs = chairsOf(lobby, lobby.you.team === 'a' ? 'b' : 'a');
    const owner = teamOwnsLobby(lobby);

    return (
      <div className="pvp-team-lobby" data-testid="pvp-team-lobby">
        {lobby.codes ? (
          /*
           * Two cards, and never one field with two values in it.
           *
           * Only the creator ever renders this: for the other five accounts the
           * server sends `codes: null`, so there is nothing here to hide.
           */
          <div className="pvp-team-codes">
            <CodeCard
              testId="ally"
              title="Código da sua equipe"
              hint="Convida os dois aliados que faltam."
              code={lobby.codes.ally}
              occupancy={`${occupied(mine)}/3`}
              copied={copied === 'ally'}
              onCopy={() => copy(lobby.codes?.ally ?? '', 'ally')}
            />
            <CodeCard
              testId="opponent"
              title="Código da equipe adversária"
              hint="Convida os três adversários."
              code={lobby.codes.opponent}
              occupancy={`${occupied(theirs)}/3`}
              copied={copied === 'opponent'}
              onCopy={() => copy(lobby.codes?.opponent ?? '', 'opponent')}
            />
          </div>
        ) : null}

        <div className="pvp-team-rosters">
          <ChairColumn
            testId="mine"
            title="Sua equipe"
            chairs={mine}
            youParticipantId={lobby.you.participantId}
          />
          <ChairColumn
            testId="theirs"
            title="Adversários"
            chairs={theirs}
            youParticipantId={lobby.you.participantId}
          />
        </div>

        {failure ? (
          <p className="pvp-error" role="alert" data-testid="pvp-team-launch-error">
            {errorText(failure)}
          </p>
        ) : null}

        {lobby.frozen ? (
          <p data-testid="pvp-team-frozen" role="status">
            SALA CONFIRMADA — entrando na arena…
          </p>
        ) : (
          <>
            <p data-testid="pvp-team-waiting" role="status">
              {lobby.full
                ? 'SEIS JOGADORES NA SALA.'
                : `AGUARDANDO JOGADORES — ${occupied(mine) + occupied(theirs)}/6.`}
            </p>
            {/*
             * The launch button exists only for the account the SERVER would
             * accept it from. That is not a permission invented here: the route
             * refuses anybody who is not the account that opened the lobby, and
             * this is the same sentence, read from the chair in `you`.
             */}
            {owner ? (
              <button
                type="button"
                data-testid="pvp-team-launch"
                onClick={onLaunch}
                disabled={launching || !teamCanLaunch(lobby)}
              >
                {launching ? 'CONFIRMANDO…' : 'INICIAR PARTIDA'}
              </button>
            ) : (
              <p className="pvp-hint" data-testid="pvp-team-owner-note">
                Quem criou a sala inicia a partida.
              </p>
            )}
          </>
        )}
        {/*
         * The hunt is still running behind this. Said out loud so nobody waiting
         * on five other people thinks they have left the game.
         */}
        <p className="pvp-hint">Sua caçada continua enquanto você espera.</p>
      </div>
    );
  }

  function copy(value: string, which: 'ally' | 'opponent') {
    /*
     * Copying is the UI's business and may simply not be available: an insecure
     * origin, a permission the player refused, an embedded webview. It must
     * never take the app down over a convenience - the code is on screen either
     * way.
     */
    void navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(which))
      .catch(() => setCopied(null));
  }

  function submitCode() {
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      return;
    }
    /*
     * The code STAYS in the field. A player who mistyped one character of ten
     * should be one keystroke from being right.
     */
    onJoin(trimmed);
  }
}

/** The chairs on one side, in slot order. Never in the order the array held. */
function chairsOf(lobby: PvpTeamLobby, team: 'a' | 'b'): PvpTeamChair[] {
  return lobby.chairs
    .filter((chair) => chair.team === team)
    .sort((one, two) => one.slot - two.slot);
}

function occupied(chairs: readonly PvpTeamChair[]): number {
  return chairs.filter((chair) => chair.occupied).length;
}

function CodeCard({
  testId,
  title,
  hint,
  code,
  occupancy,
  copied,
  onCopy,
}: {
  readonly testId: 'ally' | 'opponent';
  readonly title: string;
  readonly hint: string;
  readonly code: string;
  readonly occupancy: string;
  readonly copied: boolean;
  readonly onCopy: () => void;
}) {
  return (
    <div className={`pvp-team-code-card pvp-team-code-${testId}`}>
      <p className="pvp-code-label">{title}</p>
      <p className="pvp-code" data-testid={`pvp-team-code-${testId}`}>
        {code}
      </p>
      <p className="pvp-hint">{hint}</p>
      <p className="pvp-team-occupancy" data-testid={`pvp-team-occupancy-${testId}`}>
        {occupancy}
      </p>
      <button type="button" data-testid={`pvp-team-copy-${testId}`} onClick={onCopy}>
        COPIAR CÓDIGO
      </button>
      {copied ? (
        <span className="pvp-copied" data-testid={`pvp-team-copied-${testId}`} role="status">
          Copiado.
        </span>
      ) : null}
    </div>
  );
}

function ChairColumn({
  testId,
  title,
  chairs,
  youParticipantId,
}: {
  readonly testId: 'mine' | 'theirs';
  readonly title: string;
  readonly chairs: readonly PvpTeamChair[];
  readonly youParticipantId: string;
}) {
  return (
    <div
      className={`pvp-team-roster pvp-team-roster-${testId}`}
      data-testid={`pvp-team-column-${testId}`}
    >
      <h3>{title}</h3>
      <ol>
        {chairs.map((chair) => (
          /*
           * Keyed by the participant id the SERVER named. Nothing here is
           * draggable, selectable or swappable: a chair is the server's answer,
           * and an interface that let somebody move one would be an interface
           * making a promise the API refuses.
           */
          <li
            key={chair.participantId}
            className={`pvp-team-chair${chair.occupied ? ' pvp-team-chair-taken' : ''}`}
            data-testid={`pvp-team-chair-${chair.participantId}`}
            data-occupied={chair.occupied ? 'true' : 'false'}
          >
            <span className="pvp-team-chair-slot">Posição {chair.slot}</span>
            <span className="pvp-team-chair-state">
              {chair.participantId === youParticipantId
                ? 'VOCÊ'
                : chair.occupied
                  ? 'Ocupado'
                  : 'Aguardando'}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * A refusal, in words a player can act on.
 *
 * The server deliberately answers several situations with one reason so a code
 * cannot be used to enumerate live rooms, and this mapping keeps that. A reason
 * nobody mapped is still shown as a refusal, never as a result - and never with
 * the server's raw string, which is not written for a player.
 */
function errorText(reason: string): string {
  switch (reason) {
    case 'match_not_found':
    case 'team_lobby_gone':
      return 'Essa sala não está mais disponível.';
    case 'match_not_joinable':
    case 'lobby_full':
      return 'Essa equipe já está completa.';
    case 'already_in_match':
      return 'Você já está em uma partida.';
    case 'not_in_lobby':
      return 'Você não está nesta sala.';
    case 'not_the_lobby_owner':
      return 'Só quem criou a sala pode iniciar a partida.';
    case 'lobby_not_full':
      return 'A partida só começa com as duas equipes completas.';
    case 'lobby_not_frozen':
      return 'A sala ainda não foi confirmada.';
    case 'pvp_disabled':
    case 'pvp_gold_pot_unavailable':
      return 'O modo 3x3 não está disponível nesta versão.';
    case 'superseded':
      return 'A partida foi assumida por outra aba.';
    case 'rate_limited':
      return 'Conexão encerrada por excesso de mensagens.';
    case 'malformed':
      return 'Erro de protocolo. Recarregue a página.';
    case 'unauthorised':
    case 'not_authorised':
      return 'Sua sessão não autoriza essa partida.';
    case 'rules_version_mismatch':
      return 'Sua versão está desatualizada. Recarregue a página.';
    case 'reconnect_failed':
      return 'Não foi possível reconectar a tempo.';
    default:
      return 'Não foi possível continuar a partida.';
  }
}
